import React, { useState, useEffect } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AppBar, Toolbar, Box, Typography, IconButton, CircularProgress, Avatar } from '@mui/material';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { AuthProvider, useAuth } from './AuthContext';
import { warmCinematicTheme } from './themes';
import useStore from './store';
import SignInScreen from './components/SignInScreen';
import OnboardingFlow from './components/OnboardingFlow';
import TrackerScreen from './components/TrackerScreen';
import WatchListScreen from './components/WatchListScreen';
import SuggestScreen from './components/SuggestScreen';
import BottomNav from './components/BottomNav';
import RatingDialog from './components/RatingDialog';
import ProfileDrawer from './components/ProfileDrawer';

function AppContent() {
  const { user } = useAuth();
  const setAuthUser = useStore(s => s.setAuthUser);
  const flushNow = useStore(s => s.flushNow);
  const onboardingComplete = useStore(s => s.onboardingComplete);
  const onboardingChecked = useStore(s => s._onboardingChecked);
  const completeOnboarding = useStore(s => s.completeOnboarding);
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem('tracker_active_tab');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [ratingShow, setRatingShow] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // Persist tab choice
  useEffect(() => {
    localStorage.setItem('tracker_active_tab', tab);
  }, [tab]);

  // Sync Firebase auth user into Zustand store
  useEffect(() => {
    if (user !== undefined) setAuthUser(user ?? null);
  }, [user, setAuthUser]);

  // Flush pending Firestore writes when page is closed
  useEffect(() => {
    window.addEventListener('beforeunload', flushNow);
    return () => window.removeEventListener('beforeunload', flushNow);
  }, [flushNow]);

  if (user === undefined || (user && !onboardingChecked)) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a0805' }}>
        <CircularProgress sx={{ color: '#e8b86d' }} />
      </Box>
    );
  }

  if (!user) return <SignInScreen />;

  if (!onboardingComplete) return (
    <OnboardingFlow
      onComplete={(prefs) => {
        setTab(2);
        completeOnboarding(prefs);
      }}
    />
  );

  return (
    <Box sx={{ pb: '80px', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ minHeight: 52 }}>
          <LocalMoviesIcon sx={{ mr: 1.25, color: 'primary.main', fontSize: 20 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.15, color: 'text.primary' }}>
              British Show Butler
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', letterSpacing: 2.5, textTransform: 'uppercase', color: 'text.secondary', fontFamily: '"Inter", sans-serif' }}>
              Your Personal Detective Concierge
            </Typography>
          </Box>
          <IconButton onClick={() => setProfileOpen(true)} size="small" sx={{ p: 0.5 }}>
            {user?.photoURL
              ? <Avatar src={user.photoURL} sx={{ width: 28, height: 28 }} />
              : <AccountCircleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            }
          </IconButton>
        </Toolbar>
      </AppBar>

      {tab === 0 && <TrackerScreen  onRateShow={setRatingShow} />}
      {tab === 1 && <WatchListScreen onRateShow={setRatingShow} />}
      {tab === 2 && <SuggestScreen  onRateShow={setRatingShow} />}

      <BottomNav activeTab={tab} onChange={setTab} />

      {ratingShow && (
        <RatingDialog show={ratingShow} onClose={() => setRatingShow(null)} />
      )}

      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={warmCinematicTheme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}
